import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { GALLERY_CATEGORIES } from "@/data/content";
import { api } from "@/lib/api";
import { imgUrl } from "@/lib/api";
import { SectionHeader } from "@/pages/Home";

export default function Gallery() {
  const [cat, setCat] = React.useState("Tutte");
  const [lightbox, setLightbox] = React.useState(null); // index into filtered
  const [GALLERY, setGallery] = React.useState([]);
  React.useEffect(() => { api.get("/gallery").then((r) => setGallery(r.data)).catch(console.error); }, []);

  const filtered = React.useMemo(
    () => (cat === "Tutte" ? GALLERY : GALLERY.filter((g) => g.cat === cat)),
    [cat, GALLERY]
  );

  const open = (i) => setLightbox(i);
  const close = () => setLightbox(null);
  const prev = (e) => { e?.stopPropagation(); setLightbox((i) => (i - 1 + filtered.length) % filtered.length); };
  const next = (e) => { e?.stopPropagation(); setLightbox((i) => (i + 1) % filtered.length); };

  React.useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, filtered.length]);

  return (
    <div className="bg-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-6">
        <SectionHeader eyebrow="I nostri momenti" title="Gallery" subtitle="Le immagini dei corsi, degli eventi e dei percorsi di crescita che abbiamo vissuto insieme." />
        <div className="mt-8 flex flex-wrap gap-2" data-testid="gallery-filters">
          {GALLERY_CATEGORIES.map((c) => {
            const active = cat === c;
            return (
              <button key={c} data-testid={`gallery-filter-${c}`} onClick={() => { setCat(c); setLightbox(null); }}
                className={`px-4 py-2 rounded-full text-sm font-600 border transition-all hover:scale-[1.03] ${active ? "bg-[#003366] text-white border-[#003366]" : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"}`}>
                {c}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 sm:gap-6 [column-fill:_balance]" data-testid="gallery-grid">
          {filtered.map((g, i) => (
            <motion.button
              key={g.id}
              layout
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.4, delay: (i % 6) * 0.04 }}
              onClick={() => open(i)}
              data-testid={`gallery-item-${g.id}`}
              className="group relative mb-4 sm:mb-6 w-full break-inside-avoid overflow-hidden rounded-3xl border border-gray-100 block"
            >
              <img src={imgUrl(g.src)} alt={g.title} loading="lazy" className={`w-full object-cover transition-transform duration-500 group-hover:scale-105 ${g.tall ? "h-80" : "h-56"}`} />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "linear-gradient(180deg,rgba(0,51,102,0) 40%,rgba(0,51,102,0.7) 100%)" }} />
              <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all text-left">
                <span className="text-[10px] font-700 uppercase tracking-wide text-[#2FA84F]">{g.cat}</span>
                <p className="text-white font-600 text-sm">{g.title}</p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <AnimatePresence>
        {lightbox !== null && filtered[lightbox] && (
          <motion.div data-testid="gallery-lightbox" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4" onClick={close}>
            <button data-testid="lightbox-close" onClick={close} className="absolute top-5 right-5 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><X size={22} /></button>
            <button data-testid="lightbox-prev" onClick={prev} className="absolute left-3 sm:left-8 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronLeft size={24} /></button>
            <button data-testid="lightbox-next" onClick={next} className="absolute right-3 sm:right-8 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"><ChevronRight size={24} /></button>
            <motion.div key={filtered[lightbox].id} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.25 }} onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
              <img src={imgUrl(filtered[lightbox].src)} alt={filtered[lightbox].title} className="w-full max-h-[80vh] object-contain rounded-2xl" />
              <div className="text-center mt-4">
                <span className="text-xs font-700 uppercase tracking-wide text-[#2FA84F]">{filtered[lightbox].cat}</span>
                <p className="text-white font-600">{filtered[lightbox].title}</p>
                <p className="text-white/50 text-sm mt-1">{lightbox + 1} / {filtered.length}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
